import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router";
import { reportError } from "../utils/reportError";
import {
  attemptChunkReloadRecovery,
  attemptDomMutationRecovery,
  attemptIndexedDbRecovery,
  isChunkLoadError,
  isDomMutationError,
  isIndexedDbDisconnectError,
  isSafariEmptyRangesError,
} from "../utils/recoveryErrors";

// PostHog Error Boundary Component
export default function RootErrorBoundary() {
  const error = useRouteError();
  const isIndexedDbDisconnect =
    error instanceof Error ? isIndexedDbDisconnectError(error) : false;
  const isSafariEmptyRanges =
    error instanceof Error ? isSafariEmptyRangesError(error) : false;
  const isChunkLoadFailure =
    error instanceof Error ? isChunkLoadError(error) : false;
  const isDomMutation = error instanceof Error ? isDomMutationError(error) : false;
  const isAutoRecoverableBrowserIssue =
    isIndexedDbDisconnect || isSafariEmptyRanges || isChunkLoadFailure || isDomMutation;

  // When an IndexedDB disconnect reaches the error boundary (rather than the
  // global unhandledrejection path), trigger a one-shot reload so the user
  // doesn't have to click. The shared sessionStorage loop guard ensures we
  // only reload once — if the boundary re-renders after reload, the user sees
  // the static "Connection Interrupted" fallback with the manual button.
  useEffect(() => {
    if (isIndexedDbDisconnect) {
      attemptIndexedDbRecovery();
    }
  }, [isIndexedDbDisconnect]);

  // React Router's lazy() catches dynamic-import rejections internally and
  // hands them to the route errorElement, so the global chunk-load handler in
  // main.tsx never sees them. Mirror its one-shot reload here.
  useEffect(() => {
    if (isChunkLoadFailure) {
      attemptChunkReloadRecovery();
    }
  }, [isChunkLoadFailure]);

  // External DOM mutations (page translation etc.) that slip past the
  // domMutationGuard crash React's commit and surface here, not in the global
  // handlers. Mirror the one-shot reload.
  useEffect(() => {
    if (isDomMutation) {
      attemptDomMutationRecovery();
    }
  }, [isDomMutation]);

  // React Router's errorElement swallows the throw, so neither Sentry's nor
  // PostHog's global handlers ever see render crashes — this is their only
  // reporting path. Effect-scoped so a re-render doesn't duplicate the event.
  useEffect(() => {
    if (error && !isAutoRecoverableBrowserIssue) {
      reportError(error, {
        area: "render",
        surface: "root_error_boundary",
        tags: { error_path: "route_error_element" },
      });
    }
  }, [error, isAutoRecoverableBrowserIssue]);

  const handleReload = () => {
    window.location.reload();
  };

  if (isRouteErrorResponse(error)) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">
            {error.status} {error.statusText}
          </h1>
          <p className="text-gray-400">{error.data}</p>
        </div>
      </div>
    );
  }

  if (error instanceof Error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center max-w-md px-4">
          <h1 className="text-4xl font-bold mb-4">
            {isIndexedDbDisconnect
              ? "Connection Interrupted"
              : isSafariEmptyRanges
                ? "Temporary Browser Error"
                : isChunkLoadFailure
                  ? "Updating to the latest version"
                  : isDomMutation
                    ? "Restoring the page"
                    : "Something went wrong"}
          </h1>
          <p className="text-gray-400">
            {isIndexedDbDisconnect
              ? "Your browser storage connection was interrupted after being inactive. Reload to continue."
              : isSafariEmptyRanges
                ? "Safari hit a temporary runtime issue. Reload to continue."
                : isChunkLoadFailure
                  ? "A newer version is available. Reload to get the latest files."
                  : isDomMutation
                    ? "A browser feature (like page translation) modified the app unexpectedly. Reload to continue — translation will still work."
                    : error.message}
          </p>
          {isAutoRecoverableBrowserIssue && (
            <>
              <button
                type="button"
                onClick={handleReload}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-brand-start to-brand-end px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90"
              >
                Reload page
              </button>
              <p className="mt-3 text-sm text-gray-500">
                {isChunkLoadFailure
                  ? "If it still fails, close this tab and reopen the app."
                  : isDomMutation
                    ? "If it keeps happening, try turning off page translation for this site."
                    : "If it still fails, close this tab and reopen the app in Safari."}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center text-white">
      <h1 className="text-4xl font-bold">Unknown Error</h1>
    </div>
  );
}
